import { format, isValid, parse } from 'date-fns';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { addRendition } from '@shared/utils/photoRendition';
import { stripHtmlTags } from '@shared/utils/sanitizeHtmlContent';

/**
 * Single source of the PDP's Open Graph content derivation (title, image,
 * description, dates, canonical URL). Consumed by `StaysPdpHead` (the real
 * og: meta tags) and Share Your Stay's `useSmsLandingPreview` (the mocked
 * iMessage unfurl card) — the mock renders whatever the crawlers will see,
 * so the two must not drift. Lives in stays/utils (not beside StaysPdpHead)
 * because it crosses the EventAccommodations/PublicStays boundary.
 */

const MAX_OG_DESCRIPTION_LENGTH = 200;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const DATE_PARAM_FORMAT = 'yyyy-MM-dd';

// Structural subset of `accommodationCatalogPropertyById` — both consumers
// fetch via HotelStaticByJoyPlace, but taking the shape (not the generated
// type) keeps this file free of a .graphql.generated dependency.
export type HotelOgSource = Readonly<{
  name?: string | null;
  address?: Readonly<{ city?: string | null; state?: string | null; country?: string | null }> | null;
  photos?: ReadonlyArray<Readonly<{ url?: string | null }>> | null;
  plainTextLongDescription?: string | null;
}>;

/**
 * Strictly parses a YYYY-MM-DD query param into a local Date.
 *
 * `parse` + `isValid` alone rejects impossible calendar dates ("2026-02-30"),
 * out-of-range months, and trailing garbage ("2026-07-17-extra"), but still
 * accepts loosely-formatted inputs that it silently normalizes — e.g. "2026-7-1",
 * "26-07-17", or a trailing space. The round-trip check (`format(date) === value`)
 * requires the input to be an exact, zero-padded YYYY-MM-DD, so normalized values
 * never leak into the og:title or og:url.
 */
const parseDateParam = (value: string | null): Date | undefined => {
  if (!value) return undefined;
  const date = parse(value, DATE_PARAM_FORMAT, new Date(0));
  if (!isValid(date) || format(date, DATE_PARAM_FORMAT) !== value) return undefined;
  return date;
};

/**
 * Formats a validated date range into a compact display format.
 * Same month:   "Jul 17–19, 2026"
 * Cross-month:  "Jul 17 – Aug 2, 2026"
 * Cross-year:   "Dec 30, 2026 – Jan 2, 2027"
 */
const formatDateRange = (checkIn: Date, checkOut: Date): string => {
  const inMonth = MONTH_NAMES[checkIn.getMonth()];
  const outMonth = MONTH_NAMES[checkOut.getMonth()];
  const inD = checkIn.getDate();
  const outD = checkOut.getDate();
  const inY = checkIn.getFullYear();
  const outY = checkOut.getFullYear();

  if (inY !== outY) {
    return `${inMonth} ${inD}, ${inY} – ${outMonth} ${outD}, ${outY}`;
  }
  if (checkIn.getMonth() === checkOut.getMonth()) {
    return `${inMonth} ${inD}–${outD}, ${inY}`;
  }
  return `${inMonth} ${inD} – ${outMonth} ${outD}, ${outY}`;
};

/**
 * Validates the check-in/check-out query params once and derives both the display
 * label (for og:title) and the normalized URL params (for og:url) from the same
 * source. Returns undefined when either date is missing/malformed or check-out is
 * not strictly after check-in, so an invalid range pollutes neither the title nor URL.
 */
export const buildDateContext = (checkIn: string | null, checkOut: string | null): { label: string; checkIn: string; checkOut: string } | undefined => {
  const checkInDate = parseDateParam(checkIn);
  const checkOutDate = parseDateParam(checkOut);
  if (!checkInDate || !checkOutDate || checkInDate >= checkOutDate) return undefined;

  return {
    label: formatDateRange(checkInDate, checkOutDate),
    checkIn: format(checkInDate, DATE_PARAM_FORMAT),
    checkOut: format(checkOutDate, DATE_PARAM_FORMAT),
  };
};

/**
 * Builds a short location string from the hotel address, e.g. "New York, NY" or "Paris, France".
 */
export const buildLocationString = (address: HotelOgSource['address']): string | undefined => {
  if (!address) return undefined;
  const parts = [address.city, address.state ?? address.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
};

/**
 * og:title — "Hotel Name – City, ST" with any extra detail (e.g. a date range)
 * joined by "·": "Hotel Name – City, ST · Jul 17–19, 2026".
 */
export const buildHotelOgTitle = (property: HotelOgSource, extraDetail?: string): string => {
  const hotelName = property.name || 'Hotel';
  const details = [buildLocationString(property.address), extraDetail].filter(Boolean);
  return details.length ? `${hotelName} – ${details.join(' · ')}` : hotelName;
};

/** og:image — the first catalog photo (medium rendition), or the stock hotel image. */
export const buildHotelOgImageUrl = (property: HotelOgSource): string =>
  property.photos?.[0]?.url ? (addRendition({ url: property.photos[0].url, renditionSize: 'medium' }) as string) : DEFAULT_HOTEL_IMAGE;

/** og:description — the hotel's long description, tags stripped, capped at 200 chars. */
export const buildHotelOgDescription = (property: HotelOgSource): string | undefined => {
  const description = property.plainTextLongDescription ? stripHtmlTags(property.plainTextLongDescription) : undefined;
  if (description && description.length > MAX_OG_DESCRIPTION_LENGTH) {
    return `${description.slice(0, MAX_OG_DESCRIPTION_LENGTH)}…`;
  }
  return description;
};

/**
 * og:url — the canonical URL for a stays PDP, preserving the eventId and date query
 * params so the URL round-trips correctly when crawlers follow it. Including the dates
 * keeps date-specific shares distinct for OG consumers that key the preview on the URL.
 */
export const buildCanonicalUrl = (joyPlaceId: string, eventId: string | null, checkIn: string | null, checkOut: string | null): string => {
  const base = `https://withjoy.com/stays/${joyPlaceId}`;
  const params = new URLSearchParams();
  if (eventId) params.set('eventId', eventId);
  if (checkIn) params.set('checkin', checkIn);
  if (checkOut) params.set('checkout', checkOut);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
};
